import { useEffect, useState, type ReactNode } from "react";
import { Helmet } from "react-helmet-async";
import { Header } from "@/components/Header";
import { Breadcrumb } from "@/components/Breadcrumb";
import { Footer } from "@/components/Footer";
import { AccessibilityWidget } from "@/components/AccessibilityWidget";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Phone, Facebook, Instagram, Youtube } from "lucide-react";
import { Link } from "react-router-dom";
import { TikTokIcon, GoogleIcon } from "@/components/icons/SocialIcons";
import biagioImage from "@/assets/biagio.webp";
import ginoImage from "@/assets/gino.webp";
import aboutHeroImage from "@/assets/about-hero.webp";

const socialLinks = [
  { name: "Facebook", href: "https://www.facebook.com/profile.php?id=61557283318193", Icon: Facebook },
  { name: "Instagram", href: "https://instagram.com/masonrypro", Icon: Instagram },
  { name: "TikTok", href: "https://www.tiktok.com/@masonryproconstruction", Icon: TikTokIcon },
  { name: "YouTube", href: "https://www.youtube.com/@MasonryProConstruction", Icon: Youtube },
  { name: "Google", href: "https://share.google/5h8hZ1wmQXmPxlti5", Icon: GoogleIcon },
];

const InternalLink = ({ to, children }: { to: string; children: ReactNode }) => (
  <Link to={to} className="text-secondary hover:underline">
    {children}
  </Link>
);

const teamMembers = [
  {
    name: "Biagio",
    image: biagioImage,
    bio: (
      <>
        <p>Biagio, the owner of Masonry Pro Construction, was practically raised with a trowel in hand. Introduced to the art of masonry at just 8 years old by his father, a respected mason, Biagio has spent over 35 years honing his craft. His journey has led him to be part of hundreds of projects, each one a testament to his skill and dedication.</p>
        <p className="mt-4">Proudly carrying forward his family's legacy, Biagio is not only a master craftsman but also a committed member of the bricklayers union. Under Biagio's leadership, Masonry Pro Construction continues to build not just structures, but legacies that stand the test of time.</p>
      </>
    ),
  },
  {
    name: "Gino",
    image: ginoImage,
    bio: (
      <>
        <p>Gino has been an integral part of Masonry Pro Construction for over 14 years, learning and working side by side with his father. His journey has granted him extensive experience across a wide array of masonry projects. From <InternalLink to="/paving-services/paver-patios">paver patios</InternalLink> and <InternalLink to="/masonry-services/retaining-walls">retaining walls</InternalLink>, to <InternalLink to="/concrete-services/driveways">concrete driveways</InternalLink> and <InternalLink to="/waterproofing-services/basement-waterproofing">basement waterproofing</InternalLink>, Gino has been involved in countless structures with precision and skill.</p>
        <p className="mt-4">His dedication to Masonry Pro and his deep understanding of masonry techniques make him a valuable asset to the team, ensuring that every project at Masonry Pro Construction is executed with the highest level of quality and professionalism.</p>
      </>
    ),
  },
];

const differentiators = [
  { title: "35+ Years Experience", description: "Three generations of masonry expertise in North Jersey." },
  { title: "5-Star Google Rating", description: "Our reputation is built on results." },
  { title: "A+ BBB Rating", description: "Commitment to customer satisfaction." },
  { title: "Fully Licensed & Insured", description: (<>NJ HIC License <a href="https://newjersey.mylicense.com/verification/Search.aspx?facility=Y" target="_blank" rel="noopener noreferrer" className="hover:text-secondary transition-colors underline">#13VH06777200</a>. Protection for you and your property.</>) },
  { title: "Family-Owned", description: "Personal attention on every project." },
  { title: "Local", description: "Based in Totowa, serving North Jersey communities." },
];

export default function About() {
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
    };
    
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <>
      <Helmet>
        <title>About Masonry Pro Totowa, NJ | Masonry Pro Construction</title>
        <meta name="description" content="Family-owned masonry contractor based in Totowa, NJ since 1989. Biagio and Gino build patios, walls, and concrete work across North Jersey. Licensed, insured." />
        <meta property="og:description" content="Family-owned masonry contractor based in Totowa, NJ since 1989. Biagio and Gino build patios, walls, and concrete work across North Jersey. Licensed, insured." />
        <meta property="og:title" content="About Masonry Pro Totowa, NJ | Masonry Pro Construction" />
        <meta property="og:image" content="https://masonry-pro.com/og/about.jpg" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:type" content="image/jpeg" />
        <meta name="twitter:image" content="https://masonry-pro.com/og/about.jpg" />
      </Helmet>

      <div className="min-h-screen bg-background text-foreground">
        <Header />
        <Breadcrumb />
        
        <main>
          {/* Hero Section */}
          <section className="relative overflow-hidden" style={{ backgroundColor: '#0d1017', paddingTop: '160px', paddingBottom: '100px' }}>
            <img src={aboutHeroImage} alt="Masonry Pro Construction team at work in North Jersey" width={1200} height={800} fetchPriority="high" decoding="async" className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, rgba(13,16,23,0.45) 0%, rgba(13,16,23,0.65) 100%)' }} />
            <div className="container mx-auto px-4 text-center relative z-10">
              <h1 style={{ color: '#f5f5f5', fontSize: '3rem', fontWeight: 'bold', lineHeight: 1.1, textShadow: '0 2px 8px rgba(0,0,0,0.9)' }}>
                35+ Years of Building Trust in{" "}
                <span style={{ color: '#d4a853', WebkitTextFillColor: '#d4a853' }}>Totowa, NJ</span> Serving North Jersey
              </h1>
            </div>
          </section>

          {/* Our Story Section */}
          <section className="py-20 bg-card">
            <div className="container mx-auto px-4">
              <div className="max-w-4xl mx-auto">
                <h2 className="text-3xl md:text-4xl font-bold mb-8 text-center">
                  <span className="text-gradient-gold">Our Story</span>
                </h2>
                
                <div className="space-y-6 text-foreground/80 text-lg leading-relaxed">
                  <p className="animate-fade-up">
                    Masonry Pro Construction started over 35 years ago with a simple idea: build things right the first time. What began as a small family operation in Totowa has grown into North Jersey's trusted name in masonry, but our approach hasn't changed. You can read how that approach has held up for other homeowners on our <InternalLink to="/reviews">customer reviews page</InternalLink>.
                  </p>
                  
                  <p className="animate-fade-up" style={{ animationDelay: "0.1s" }}>
                    We still treat every project like we're building it for our own home. We still show up when we say we will. And we still stand behind our work long after the last stone is set.
                  </p>
                  
                  <p className="animate-fade-up" style={{ animationDelay: "0.2s" }}>
                    Over three decades, we've seen what works in NJ conditions and what doesn't. We've learned that shortcuts in base preparation come back as callbacks. That drainage isn't optional. That the difference between masonry that lasts and masonry that fails is almost always invisible once the project is done.
                  </p>
                  
                  <p className="animate-fade-up" style={{ animationDelay: "0.3s" }}>
                    That's why we photograph every layer. That's why we build drainage into every <InternalLink to="/masonry-services/retaining-walls">retaining wall</InternalLink>. That's why homeowners across <InternalLink to="/service-areas/bergen-county">Bergen</InternalLink>, <InternalLink to="/service-areas/essex-county">Essex</InternalLink>, and <InternalLink to="/service-areas/passaic-county">Passaic</InternalLink> counties keep calling us back, whether it's a <InternalLink to="/paving-services/paver-patios">paver patio</InternalLink>, a set of <InternalLink to="/masonry-services/steps-stoops">front steps</InternalLink>, a <InternalLink to="/concrete-services/foundations">concrete foundation</InternalLink>, or a <InternalLink to="/masonry-services/fire-pits">fireplace and fire pit</InternalLink>.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Meet Us Section */}
          <section className="py-20 bg-background">
            <div className="container mx-auto px-4">
              <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center">
                <span className="text-gradient-gold">Meet Us</span>
              </h2>
              
              <div className="grid md:grid-cols-2 gap-12 max-w-5xl mx-auto">
                {teamMembers.map((member, index) => (
                  <div 
                    key={member.name}
                    className="flex flex-col items-center text-center animate-fade-up"
                    style={{ animationDelay: `${index * 0.1}s` }}
                  >
                    <div className="relative mb-6">
                      <div className="w-64 h-64 rounded-full overflow-hidden border-4 border-secondary/30 shadow-gold">
                        <img
                          src={member.image}
                          alt={`Meet ${member.name}`}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    </div>

                    <h3 className="text-2xl md:text-3xl font-bold mb-4">
                      Meet {member.name}
                    </h3>

                    <div className="text-foreground/70 leading-relaxed">
                      {member.bio}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* See Our Work CTA */}
          <section className="py-12 bg-card">
            <div className="container mx-auto px-4 text-center">
              <Button variant="gold" size="xl" asChild>
                <Link to="/gallery" className="inline-flex items-center gap-2">
                  See Our Work →
                </Link>
              </Button>
            </div>
          </section>

          {/* What Sets Us Apart Section */}
          <section className="py-20 bg-card">
            <div className="container mx-auto px-4">
              <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center">
                <span className="text-gradient-gold">What Sets Us Apart</span>
              </h2>
              
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
                {differentiators.map((item, index) => (
                  <div 
                    key={item.title}
                    className="flex items-start gap-4 p-6 bg-background rounded-xl border border-border/50 animate-fade-up"
                    style={{ animationDelay: `${index * 0.05}s` }}
                  >
                    <CheckCircle2 className="h-6 w-6 text-secondary flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="font-bold text-foreground mb-1">{item.title}</h3>
                      <p className="text-foreground/70 text-sm">{item.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Find Us On Section */}
          <section className="py-20 bg-background">
            <div className="container mx-auto px-4">
              <h2 className="text-6xl md:text-8xl font-bold mb-12 text-center">
                <span className="text-gradient-gold">Find Us On</span>
              </h2>
              
              <div className="flex flex-wrap justify-center gap-6 max-w-2xl mx-auto animate-fade-up">
                {socialLinks.map((social) => (
                  <a
                    key={social.name}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group flex flex-col items-center gap-2"
                    aria-label={`Visit our ${social.name} page`}
                  >
                    <div className="w-14 h-14 md:w-16 md:h-16 rounded-full bg-card border border-border/50 flex items-center justify-center transition-all duration-300 group-hover:border-secondary group-hover:shadow-gold">
                      <social.Icon className="h-6 w-6 md:h-7 md:w-7 text-foreground/70 transition-colors group-hover:text-secondary" />
                    </div>
                    <span className="text-xs text-muted-foreground group-hover:text-secondary transition-colors">
                      {social.name}
                    </span>
                  </a>
                ))}
              </div>
            </div>
          </section>

          {/* CTA Section */}
          <section className="py-20 bg-gradient-navy">
            <div className="container mx-auto px-4 text-center">
              <h2 className="text-3xl md:text-4xl font-bold mb-8 text-primary-foreground">
                Ready to Work with North Jersey's Trusted Masons?
              </h2>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button variant="gold" size="xl">
                  Schedule Your Free Estimate
                </Button>
                <Button variant="goldOutline" size="xl" asChild>
                  <a href="tel:+19737252395" className="flex items-center gap-2">
                    <Phone className="h-5 w-5" />
                    Call or Text (973) 725-2395
                  </a>
                </Button>
              </div>
            </div>
          </section>
        </main>

        <Footer />
        <AccessibilityWidget />
      </div>
    </>
  );
}
